import { array, eq, nonEmptyArray } from 'fp-ts';
import { flow, identity, pipe } from 'fp-ts/function';
import * as NEA from 'fp-ts/NonEmptyArray';
import * as apply from './apply.js';
export const makeBy = NEA.makeBy;
export const of = NEA.of;
export const range = NEA.range;
export const replicate = NEA.replicate;
export const fromArray = NEA.fromArray;
export const fromReadonlyNonEmptyArray = NEA.fromReadonlyNonEmptyArray;
export const Do = NEA.Do;
export const apS = NEA.apS;
export const bind = NEA.bind;
export const bindTo = NEA.bindTo;
export const alt = NEA.alt;
export const altW = NEA.altW;
export const foldMap = NEA.foldMap;
export const foldMapWithIndex = NEA.foldMapWithIndex;
export const reduce = NEA.reduce;
export const reduceRight = NEA.reduceRight;
export const reduceRightWithIndex = NEA.reduceRightWithIndex;
export const reduceWithIndex = NEA.reduceWithIndex;
export const Alt = NEA.Alt;
export const Applicative = NEA.Applicative;
export const Apply = NEA.Apply;
export const Chain = NEA.Chain;
export const Comonad = NEA.Comonad;
export const Foldable = NEA.Foldable;
export const FoldableWithIndex = NEA.FoldableWithIndex;
export const Functor = NEA.Functor;
export const FunctorWithIndex = NEA.FunctorWithIndex;
export const Monad = NEA.Monad;
export const Pointed = NEA.Pointed;
export const Traversable = NEA.Traversable;
export const TraversableWithIndex = NEA.TraversableWithIndex;
export const getEq = NEA.getEq;
export const getSemigroup = NEA.getSemigroup;
export const getShow = NEA.getShow;
export const chain = NEA.chain;
export const flap = NEA.flap;
export const map = NEA.map;
export const mapWithIndex = NEA.mapWithIndex;
export const matchLeft = NEA.matchLeft;
export const matchRight = NEA.matchRight;
export const chainFirst = NEA.chainFirst;
export const chainWithIndex = NEA.chainWithIndex;
export const flatMap = NEA.flatMap;
export const flatten = NEA.flatten;
export const traverseWithIndex = NEA.traverseWithIndex;
export const sequence = NEA.sequence;
export const traverse = NEA.traverse;
export const URI = NEA.URI;
export const ap = NEA.ap;
export const apFirst = NEA.apFirst;
export const apSecond = NEA.apSecond;
export const chop = NEA.chop;
export const chunksOf = NEA.chunksOf;
// eslint-disable-next-line @typescript-eslint/no-deprecated
export const concat = NEA.concat;
export const concatAll = NEA.concatAll;
export const concatW = NEA.concatW;
export const copy = NEA.copy;
export const duplicate = NEA.duplicate;
export const extend = NEA.extend;
export const extract = NEA.extract;
export const getUnionSemigroup = NEA.getUnionSemigroup;
export const group = NEA.group;
export const head = NEA.head;
export const init = NEA.init;
export const insertAt = NEA.insertAt;
export const intercalate = NEA.intercalate;
export const intersperse = NEA.intersperse;
export const last = NEA.last;
export const modifyAt = NEA.modifyAt;
export const modifyHead = NEA.modifyHead;
export const modifyLast = NEA.modifyLast;
export const prependAll = NEA.prependAll;
export const reverse = NEA.reverse;
export const rotate = NEA.rotate;
export const sort = NEA.sort;
export const sortBy = NEA.sortBy;
export const splitAt = NEA.splitAt;
export const tail = NEA.tail;
export const unappend = NEA.unappend;
export const union = NEA.union;
export const uniq = NEA.uniq;
export const unprepend = NEA.unprepend;
export const unzip = NEA.unzip;
export const updateAt = NEA.updateAt;
export const updateHead = NEA.updateHead;
export const updateLast = NEA.updateLast;
export const zip = NEA.zip;
export const zipWith = NEA.zipWith;
export const every = array.every;
export function sum(as, acc_) {
    const acc = acc_ ?? identity;
    return pipe(as, NEA.reduce(0, (accumulator, a) => accumulator + acc(a)));
}
/**
 * Group a non-empty array by a key.
 * @param equal the eq typeclass instance for the key
 * @param k the function to derive the key
 * @param f the function to combine the sub-array and the key to build group elements
 */
export const groupByKey = (equal, k, f) => (as) => pipe(as, NEA.group(pipe(equal, eq.contramap(k))), NEA.map((a) => f(a, k(NEA.head(a)))));
/**
 * {@link NEA.groupSort} was deprecated, this is our own implementation of this useful combinator
 */
export const groupSort = (ord) => (as) => NEA.group(ord)(NEA.sort(ord)(as));
/**
 * {@link NEA.filter} was deprecated, this is our own implementation of this useful combinator.
 */
export const filter = (p) => flow(array.filter(p), NEA.fromArray);
/**
 * This is a more versatile typing of {@link NEA.groupBy} that can deal with record keys of a subtype
 * of string. Note: This is unsafe when using it with a K that is a string literal union:
 *
 * @example
 * const evenOrOdd = (n: number): 'even' | 'odd' => n % 2 ? 'odd' : 'even';
 *
 * const groups = pipe(
 *   [2, 4, 6],
 *   groupBy(evenOrOdd),
 * );
 *
 * groups.odd[0]; // ERROR: Property 'odd' is undefined!
 *
 * // TS allows direct access to .odd, because typeof group is Record<'even' | 'odd', NonEmptyArray<number>>,
 * // which is the same as { even: NonEmptyArray<number>; odd: NonEmptyArray<number> }
 *
 *
 * Instead, use it for things like this:
 * @example
 *
 * const tasksByExerciseId = pipe(
 *   tasks,
 *   groupBy((t): ExerciseId => t.exerciseId),
 * );
 *
 * const exerciseId: ExerciseId = ...;
 * const submissionId: SubmissionId = ...;
 *
 * tasksByExerciseId[exerciseId]; // OK!
 * tasksByExerciseId[submissionId]; // ERROR: SubmissionId is not assignable to ExerciseId!
 */
export const groupBy = NEA.groupBy;
/**
 * Returns true iff the array contains exactly one element.
 * Asserts that the array is non-empty.
 */
export const isSingleton = (a) => a.length === 1;
export const matchSingletonW = ({ onSingleton, otherwise, }) => (as) => isSingleton(as) ? onSingleton(as[0]) : otherwise(as);
export const matchSingleton = matchSingletonW;
export const getUniqueOrderedSemigroup = (o) => {
    const unionO = NEA.union(o);
    const sortO = NEA.sort(o);
    return {
        concat: (x, y) => pipe(unionO(y)(x), sortO),
    };
};
export const foldMapM = (F, S) => (f) => (as) => pipe(nonEmptyArray.tail(as), array.reduce(f(nonEmptyArray.head(as)), (acc, a) => apply.map2(F)(acc, f(a))(S.concat)));
export const from = (...as) => as;
export const shuffle = (array) => {
    const source = nonEmptyArray.copy(array);
    for (let i = source.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        // it is ensured that these values exists
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        [source[i], source[j]] = [source[j], source[i]];
    }
    return source;
};
export const max = NEA.max;
export const min = NEA.min;
//# sourceMappingURL=nonEmptyArray.js.map